import { ReactNode, StrictMode, createContext, useContext, useEffect, useMemo } from 'react'
// KAN-2035 — the @dhruvanta/ui layer MUST load before the app's own stylesheet so the
// app's tokens.css can override the shared names it deliberately keeps (--radius-*,
// --shadow-*) while inheriting the retail product palette. Do not reorder.
import '@/styles/designSystem'
import '@/index.css'
import { AuthProvider } from '@context/AuthContext'
import { CartProvider } from '@context/CartContext'
import { Analytics } from '@vercel/analytics/react'
import { ErrorBoundary } from '@components/common/ErrorBoundary'
import { PortalKey } from '@/types'

interface PortalProvidersProps {
  portal: PortalKey
  enableCart?: boolean
  children: ReactNode
}

interface PortalRuntimeContext {
  portal: PortalKey
}

const PortalContext = createContext<PortalRuntimeContext | undefined>(undefined)

export const usePortalRuntime = (): PortalRuntimeContext => {
  const context = useContext(PortalContext)

  if (!context) {
    throw new Error('usePortalRuntime must be used within PortalProviders')
  }

  return context
}

export function PortalProviders({ portal, enableCart = false, children }: PortalProvidersProps) {
  const runtimeValue = useMemo<PortalRuntimeContext>(() => ({ portal }), [portal])

  useEffect(() => {
    document.body.dataset.portal = portal
    return () => {
      delete document.body.dataset.portal
    }
  }, [portal])

  // KAN-2035 — belt-and-braces: apps/<portal>/index.html already ships
  // `<html data-product="retail">` so the theme is right before first paint, but a
  // host that mounts a portal into an existing document (or a test harness) would
  // otherwise render with the neutral default palette.
  useEffect(() => {
    const html = document.documentElement
    if (html.getAttribute('data-product') !== 'retail') {
      html.setAttribute('data-product', 'retail')
    }
  }, [])

  const content = enableCart ? <CartProvider>{children}</CartProvider> : children

  // ErrorBoundary wraps the whole portal tree (including the providers) so any render-time
  // throw — even one originating in a provider — degrades to the friendly fallback instead of
  // white-screening the live storefront. Analytics stays outside the boundary so beacons keep
  // firing even on the error page.
  return (
    <StrictMode>
      <PortalContext.Provider value={runtimeValue}>
        <ErrorBoundary>
          <AuthProvider portal={portal}>{content}</AuthProvider>
        </ErrorBoundary>
        <Analytics />
      </PortalContext.Provider>
    </StrictMode>
  )
}
